'use client';

import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { useFeatureImagePlayback } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/hooks/useFeatureImagePlayback';
import { useFeatureImageReadiness } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/hooks/useFeatureImageReadiness';
import { useThumbnailCarousel } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/hooks/useThumbnailCarousel';
import { useVideoPlaybackState } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/hooks/useVideoPlaybackState';
import type { SpartanProductFeature } from '@/app/lcv/components/sections/spartan/products/shared/feature-carousel/types';
import { SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS } from '@/app/lcv/components/sections/spartan/shared/hooks/mediaProgress';
import { usePageActivity } from '@/app/lcv/components/sections/spartan/shared/hooks/usePageActivity';
type PlaybackMode = 'auto' | 'manual' | 'paused';
const FEATURE_CAROUSEL_DURATION = 32;
interface UseFeatureCarouselOptions {
  isExternallyPaused?: boolean;
  imageDurationMs?: number;
  loop?: boolean;
  visibilityThreshold?: number;
  onImageProgress: (index: number, progress: number) => void;
}
export const useFeatureCarousel = (features, t0) => {
  const $ = _c(114);
  const {
    isExternallyPaused: t1,
    imageDurationMs: t2,
    loop: t3,
    visibilityThreshold: t4,
    onImageProgress
  } = t0;
  const isExternallyPaused = t1 === undefined ? false : t1;
  const imageDurationMs = t2 === undefined ? SPARTAN_MEDIA_DEFAULT_IMAGE_DURATION_MS : t2;
  const loop = t3 === undefined ? true : t3;
  const visibilityThreshold = t4 === undefined ? 0.6 : t4;
  const completedVideoIndexRef = useRef(null);
  const selectedIndexRef = useRef(0);
  const wasPlaybackPausedRef = useRef(true);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [playbackMode, setPlaybackMode] = useState("auto");
  let t5;
  if ($[0] !== visibilityThreshold) {
    t5 = {
      threshold: visibilityThreshold,
      triggerOnce: false,
      initialInView: false
    };
    $[0] = visibilityThreshold;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const {
    ref: setMediaInViewRef,
    inView
  } = useInView(t5);
  let t6;
  if ($[2] !== features || $[3] !== loop || $[4] !== selectedIndex) {
    t6 = {
      features,
      selectedIndex,
      loop
    };
    $[2] = features;
    $[3] = loop;
    $[4] = selectedIndex;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const {
    nextIndex,
    canSelectFeature,
    canSelectPrevious,
    canSelectNext,
    watchDrag,
    handleImageSettled
  } = useFeatureImageReadiness(t6);
  let t7;
  if ($[6] !== loop || $[7] !== watchDrag) {
    t7 = {
      loop,
      align: "start",
      duration: FEATURE_CAROUSEL_DURATION,
      watchDrag
    };
    $[6] = loop;
    $[7] = watchDrag;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const [setEmblaViewportRef, emblaApi] = useEmblaCarousel(t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      rootMargin: "400px 0px",
      threshold: 0,
      triggerOnce: false,
      initialInView: false
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const {
    ref: wrapperRef,
    inView: isNearViewport
  } = useInView(t8);
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)", true) ?? true;
  const isPageActive = usePageActivity();
  const {
    videoPlaybackState,
    setVideoPlaybackState,
    handleVideoPlaying,
    handleVideoPause,
    handleVideoWaiting,
    handleVideoSeeking,
    handleVideoSeeked,
    handleVideoError
  } = useVideoPlaybackState(selectedIndexRef);
  let t9;
  if ($[10] !== emblaApi || $[11] !== features || $[12] !== imageDurationMs || $[13] !== onImageProgress || $[14] !== selectedIndex) {
    t9 = {
      emblaApi,
      features,
      selectedIndex,
      selectedIndexRef,
      imageDurationMs,
      onImageProgress
    };
    $[10] = emblaApi;
    $[11] = features;
    $[12] = imageDurationMs;
    $[13] = onImageProgress;
    $[14] = selectedIndex;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const {
    isCompleted: isImageCompleted,
    startPlayback: startImagePlayback,
    pausePlayback: pauseImagePlayback,
    resetPlayback: resetImagePlayback,
    cancelScheduledAdvance
  } = useFeatureImagePlayback(t9);
  let t10;
  if ($[16] !== features.length || $[17] !== selectedIndex) {
    t10 = {
      featureCount: features.length,
      selectedIndex
    };
    $[16] = features.length;
    $[17] = selectedIndex;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const {
    emblaRef: thumbnailEmblaRef,
    overflows: thumbnailOverflows
  } = useThumbnailCarousel(t10);
  let t11;
  if ($[19] !== setEmblaViewportRef || $[20] !== setMediaInViewRef) {
    t11 = node => {
      setMediaInViewRef(node);
      setEmblaViewportRef(node);
    };
    $[19] = setEmblaViewportRef;
    $[20] = setMediaInViewRef;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const emblaRef = t11;
  let t12;
  if ($[22] !== canSelectFeature || $[23] !== emblaApi || $[24] !== pauseImagePlayback) {
    t12 = index => {
      if (!emblaApi || index === selectedIndexRef.current || !canSelectFeature(index)) {
        return;
      }
      pauseImagePlayback();
      emblaApi.scrollTo(index);
    };
    $[22] = canSelectFeature;
    $[23] = emblaApi;
    $[24] = pauseImagePlayback;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const selectSlide = t12;
  let t13;
  if ($[26] !== canSelectPrevious || $[27] !== emblaApi || $[28] !== pauseImagePlayback) {
    t13 = () => {
      if (!emblaApi || !canSelectPrevious) {
        return;
      }
      pauseImagePlayback();
      emblaApi.scrollPrev();
    };
    $[26] = canSelectPrevious;
    $[27] = emblaApi;
    $[28] = pauseImagePlayback;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const selectPreviousSlide = t13;
  let t14;
  if ($[30] !== canSelectNext || $[31] !== emblaApi || $[32] !== pauseImagePlayback) {
    t14 = () => {
      if (!emblaApi || !canSelectNext) {
        return;
      }
      pauseImagePlayback();
      emblaApi.scrollNext();
    };
    $[30] = canSelectNext;
    $[31] = emblaApi;
    $[32] = pauseImagePlayback;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  const selectNextSlide = t14;
  let t15;
  if ($[34] !== pauseImagePlayback) {
    t15 = () => {
      setPlaybackMode("paused");
      pauseImagePlayback();
    };
    $[34] = pauseImagePlayback;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const pausePlayback = t15;
  let t16;
  if ($[36] !== cancelScheduledAdvance || $[37] !== features || $[38] !== isImageCompleted || $[39] !== resetImagePlayback || $[40] !== selectedIndex || $[41] !== setVideoPlaybackState) {
    t16 = () => {
      cancelScheduledAdvance();
      if (!features[selectedIndex]?.video?.desktop.src && isImageCompleted) {
        resetImagePlayback();
      }
      if (features[selectedIndex]?.video?.desktop.src) {
        completedVideoIndexRef.current = null;
        setVideoPlaybackState("starting");
      }
      setPlaybackMode("manual");
    };
    $[36] = cancelScheduledAdvance;
    $[37] = features;
    $[38] = isImageCompleted;
    $[39] = resetImagePlayback;
    $[40] = selectedIndex;
    $[41] = setVideoPlaybackState;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  const resumePlayback = t16;
  let t17;
  if ($[43] !== resetImagePlayback || $[44] !== setVideoPlaybackState) {
    t17 = nextIndex_0 => {
      if (nextIndex_0 === selectedIndexRef.current) {
        return;
      }
      completedVideoIndexRef.current = null;
      selectedIndexRef.current = nextIndex_0;
      resetImagePlayback();
      setSelectedIndex(nextIndex_0);
      setPlaybackMode("auto");
      setVideoPlaybackState("idle");
    };
    $[43] = resetImagePlayback;
    $[44] = setVideoPlaybackState;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  const commitSelectedIndex = t17;
  let t18;
  if ($[46] !== cancelScheduledAdvance || $[47] !== commitSelectedIndex || $[48] !== emblaApi || $[49] !== features.length || $[50] !== loop || $[51] !== setVideoPlaybackState) {
    t18 = index_0 => {
      if (index_0 !== selectedIndexRef.current) {
        return;
      }
      if (completedVideoIndexRef.current === index_0) {
        return;
      }
      completedVideoIndexRef.current = index_0;
      cancelScheduledAdvance();
      const nextIndex_1 = index_0 + 1 < features.length ? index_0 + 1 : loop ? 0 : -1;
      if (!emblaApi || nextIndex_1 < 0 || nextIndex_1 === index_0) {
        setVideoPlaybackState("ended");
        return;
      }
      commitSelectedIndex(nextIndex_1);
      emblaApi.scrollTo(nextIndex_1);
    };
    $[46] = cancelScheduledAdvance;
    $[47] = commitSelectedIndex;
    $[48] = emblaApi;
    $[49] = features.length;
    $[50] = loop;
    $[51] = setVideoPlaybackState;
    $[52] = t18;
  } else {
    t18 = $[52];
  }
  const handleVideoEnded = t18;
  let t19;
  if ($[53] !== features || $[54] !== isNearViewport || $[55] !== nextIndex || $[56] !== selectedIndex) {
    t19 = index_1 => {
      if (!isNearViewport || !features[index_1]?.video?.desktop.src) {
        return false;
      }
      return index_1 === selectedIndex || index_1 === nextIndex;
    };
    $[53] = features;
    $[54] = isNearViewport;
    $[55] = nextIndex;
    $[56] = selectedIndex;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  const shouldPreloadVideo = t19;
  let t20;
  let t21;
  if ($[58] !== commitSelectedIndex || $[59] !== emblaApi) {
    t20 = () => {
      if (!emblaApi) {
        return;
      }
      const handleSelect = () => {
        commitSelectedIndex(emblaApi.selectedScrollSnap());
      };
      const handleReInit = () => {
        commitSelectedIndex(emblaApi.selectedScrollSnap());
      };
      setSelectedIndex(emblaApi.selectedScrollSnap());
      selectedIndexRef.current = emblaApi.selectedScrollSnap();
      emblaApi.on("select", handleSelect);
      emblaApi.on("reInit", handleReInit);
      return () => {
        emblaApi.off("select", handleSelect);
        emblaApi.off("reInit", handleReInit);
      };
    };
    t21 = [commitSelectedIndex, emblaApi];
    $[58] = commitSelectedIndex;
    $[59] = emblaApi;
    $[60] = t20;
    $[61] = t21;
  } else {
    t20 = $[60];
    t21 = $[61];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[62] !== emblaApi || $[63] !== features.length || $[64] !== resetImagePlayback || $[65] !== selectedIndex || $[66] !== setVideoPlaybackState) {
    t22 = () => {
      if (selectedIndex < features.length) {
        return;
      }
      completedVideoIndexRef.current = null;
      selectedIndexRef.current = 0;
      resetImagePlayback();
      setSelectedIndex(0);
      setPlaybackMode("auto");
      setVideoPlaybackState("idle");
      emblaApi?.scrollTo(0, true);
    };
    t23 = [emblaApi, features.length, resetImagePlayback, selectedIndex, setVideoPlaybackState];
    $[62] = emblaApi;
    $[63] = features.length;
    $[64] = resetImagePlayback;
    $[65] = selectedIndex;
    $[66] = setVideoPlaybackState;
    $[67] = t22;
    $[68] = t23;
  } else {
    t22 = $[67];
    t23 = $[68];
  }
  useEffect(t22, t23);
  const hasActiveVideo = !!features[selectedIndex]?.video?.desktop.src;
  const isActiveImageReady = hasActiveVideo || canSelectNext;
  const isPlaybackEligible = !isExternallyPaused && isPageActive && inView && isActiveImageReady;
  const isPlaybackPaused = !isPlaybackEligible || playbackMode === "paused" || prefersReducedMotion && playbackMode !== "manual";
  const isVideoPlaybackActive = hasActiveVideo && videoPlaybackState === "playing";
  const isActiveVideoEnded = hasActiveVideo && videoPlaybackState === "ended";
  const isImagePlaybackActive = !!features[selectedIndex] && !features[selectedIndex]?.video?.desktop.src && features.length > 1 && !isPlaybackPaused;
  const shouldResumeVideo = hasActiveVideo && playbackMode !== "paused" && (!prefersReducedMotion || playbackMode === "manual") && (isExternallyPaused || !isPageActive || !inView);
  const isVideoStarting = hasActiveVideo && (!isPlaybackPaused && (videoPlaybackState === "idle" || videoPlaybackState === "starting" || videoPlaybackState === "waiting") || videoPlaybackState === "paused" && shouldResumeVideo);
  let t24;
  let t25;
  if ($[69] !== hasActiveVideo || $[70] !== isPlaybackPaused || $[71] !== setVideoPlaybackState) {
    t24 = () => {
      const wasPlaybackPaused = wasPlaybackPausedRef.current;
      wasPlaybackPausedRef.current = isPlaybackPaused;
      if (!wasPlaybackPaused || isPlaybackPaused || !hasActiveVideo) {
        return;
      }
      setVideoPlaybackState(_temp);
    };
    t25 = [hasActiveVideo, isPlaybackPaused, setVideoPlaybackState];
    $[69] = hasActiveVideo;
    $[70] = isPlaybackPaused;
    $[71] = setVideoPlaybackState;
    $[72] = t24;
    $[73] = t25;
  } else {
    t24 = $[72];
    t25 = $[73];
  }
  useLayoutEffect(t24, t25);
  let t26;
  let t27;
  if ($[74] !== features || $[75] !== isPlaybackPaused || $[76] !== pauseImagePlayback || $[77] !== selectedIndex || $[78] !== startImagePlayback) {
    t26 = () => {
      const activeFeature = features[selectedIndex];
      const shouldPlayImage = !!activeFeature && !activeFeature.video?.desktop.src && features.length > 1 && !isPlaybackPaused;
      if (shouldPlayImage) {
        startImagePlayback();
        return;
      }
      pauseImagePlayback();
    };
    t27 = [features, isPlaybackPaused, pauseImagePlayback, selectedIndex, startImagePlayback];
    $[74] = features;
    $[75] = isPlaybackPaused;
    $[76] = pauseImagePlayback;
    $[77] = selectedIndex;
    $[78] = startImagePlayback;
    $[79] = t26;
    $[80] = t27;
  } else {
    t26 = $[79];
    t27 = $[80];
  }
  useEffect(t26, t27);
  const t28 = emblaApi as EmblaCarouselType | undefined;
  const t29 = isPlaybackPaused || isActiveVideoEnded;
  let t30;
  if ($[81] !== canSelectFeature || $[82] !== canSelectNext || $[83] !== canSelectPrevious || $[84] !== emblaRef || $[85] !== handleImageSettled || $[86] !== handleVideoEnded || $[87] !== handleVideoError || $[88] !== handleVideoPause || $[89] !== handleVideoPlaying || $[90] !== handleVideoSeeked || $[91] !== handleVideoSeeking || $[92] !== handleVideoWaiting || $[93] !== inView || $[94] !== isImagePlaybackActive || $[95] !== isNearViewport || $[96] !== isPlaybackEligible || $[97] !== isVideoPlaybackActive || $[98] !== isVideoStarting || $[99] !== pauseImagePlayback || $[100] !== pausePlayback || $[101] !== prefersReducedMotion || $[102] !== resumePlayback || $[103] !== selectNextSlide || $[104] !== selectPreviousSlide || $[105] !== selectSlide || $[106] !== selectedIndex || $[107] !== shouldPreloadVideo || $[108] !== t28 || $[109] !== t29 || $[110] !== thumbnailEmblaRef || $[111] !== thumbnailOverflows || $[112] !== wrapperRef) {
    t30 = {
      wrapperRef,
      emblaRef,
      emblaApi: t28,
      thumbnailEmblaRef,
      thumbnailOverflows,
      inView,
      isNearViewport,
      isPlaybackEligible,
      canSelectFeature,
      canSelectPrevious,
      canSelectNext,
      selectSlide,
      selectPreviousSlide,
      selectNextSlide,
      selectedIndex,
      cancelAutoAdvance: pauseImagePlayback,
      handleVideoEnded,
      handleImageSettled,
      shouldPreloadVideo,
      isImagePlaybackActive,
      prefersReducedMotion,
      isVideoPlaybackActive,
      isVideoStarting,
      isVideoExternallyPaused: t29,
      pausePlayback,
      resumePlayback,
      handleVideoPlaying,
      handleVideoPause,
      handleVideoWaiting,
      handleVideoSeeking,
      handleVideoSeeked,
      handleVideoError
    };
    $[81] = canSelectFeature;
    $[82] = canSelectNext;
    $[83] = canSelectPrevious;
    $[84] = emblaRef;
    $[85] = handleImageSettled;
    $[86] = handleVideoEnded;
    $[87] = handleVideoError;
    $[88] = handleVideoPause;
    $[89] = handleVideoPlaying;
    $[90] = handleVideoSeeked;
    $[91] = handleVideoSeeking;
    $[92] = handleVideoWaiting;
    $[93] = inView;
    $[94] = isImagePlaybackActive;
    $[95] = isNearViewport;
    $[96] = isPlaybackEligible;
    $[97] = isVideoPlaybackActive;
    $[98] = isVideoStarting;
    $[99] = pauseImagePlayback;
    $[100] = pausePlayback;
    $[101] = prefersReducedMotion;
    $[102] = resumePlayback;
    $[103] = selectNextSlide;
    $[104] = selectPreviousSlide;
    $[105] = selectSlide;
    $[106] = selectedIndex;
    $[107] = shouldPreloadVideo;
    $[108] = t28;
    $[109] = t29;
    $[110] = thumbnailEmblaRef;
    $[111] = thumbnailOverflows;
    $[112] = wrapperRef;
    $[113] = t30;
  } else {
    t30 = $[113];
  }
  return t30;
};
function _temp(state) {
  return state === "paused" ? "starting" : state;
}